import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Phone, Mail, MapPin } from "lucide-react";

const Footer = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      id="contact"
      className="bg-charcoal pt-24 lg:pt-32"
      data-testid="footer-section"
    >
      {/* Final CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24 border-b border-white/10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight tracking-tighter mb-8">
            Need Legal Assistance?
            <br />
            <span className="text-brand-gold">Let's Get Started.</span>
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              onClick={() => scrollToSection("#lead-capture")}
              className="btn-gold px-8 py-4 text-base font-semibold uppercase tracking-wider rounded-none"
              data-testid="footer-book-consultation-btn"
            >
              Book Consultation
            </Button>
            <a
              href="tel:+27415821022"
              className="btn-outline-gold px-8 py-4 text-base font-semibold uppercase tracking-wider rounded-none inline-flex items-center justify-center"
              data-testid="footer-call-now-btn"
            >
              <Phone className="h-5 w-5 mr-2" />
              Call Now
            </a>
            <a
              href="https://wa.me/27624313194"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-4 text-base font-semibold uppercase tracking-wider rounded-none inline-flex items-center justify-center transition-colors"
              data-testid="footer-whatsapp-btn"
            >
              WhatsApp
            </a>
          </div>
        </motion.div>
      </div>

      {/* Contact Information */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        {/* Logo & Description */}
        <div className="mb-12">
          <img
            src="https://customer-assets.emergentagent.com/job_mheshe-legal/artifacts/1xa23sin_Mheshe%20Attorneys.png"
            alt="Mheshe Attorneys"
            className="h-20 w-auto mb-4"
          />
          <p className="text-white/60 font-sans text-sm leading-relaxed max-w-md">
            A 100% Black-Women owned law firm committed to delivering exceptional
            legal services with integrity and professionalism.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
          {/* Gqeberha Office */}
          <div>
            <h4 className="text-white font-serif text-xl mb-6">
              Gqeberha Office
            </h4>
            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-brand-gold mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-white/60 font-sans text-sm">
                  2-4 Clevedon Road, Mt Croix
                  <br />
                  Office 2, Clevedon Office Block
                  <br />
                  Ground Floor
                </p>
              </div>
              <div className="mt-3 overflow-hidden border border-white/10">
                <iframe
                  src="https://www.google.com/maps?q=2-4+Clevedon+Road,+Mt+Croix,+Gqeberha,+South+Africa&output=embed"
                  width="100%" height="120" style={{border: 0}} allowFullScreen="" loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" title="Gqeberha Office"
                  className="opacity-70 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>

          {/* Johannesburg Office */}
          <div>
            <h4 className="text-white font-serif text-xl mb-6">
              Johannesburg Office
            </h4>
            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-brand-gold mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-white/60 font-sans text-sm">
                  18 Marais Street
                  <br />
                  Rynfield, Benoni
                  <br />
                  1501
                </p>
              </div>
              <div className="mt-3 overflow-hidden border border-white/10">
                <iframe
                  src="https://www.google.com/maps?q=18+Marais+Street,+Rynfield,+Benoni,+South+Africa&output=embed"
                  width="100%" height="120" style={{border: 0}} allowFullScreen="" loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" title="Johannesburg Office"
                  className="opacity-70 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>

          {/* Cape Town Office */}
          <div>
            <h4 className="text-white font-serif text-xl mb-6">
              Cape Town Office
            </h4>
            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-brand-gold mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-white/60 font-sans text-sm">
                  12 Peter Cloete Street
                  <br />
                  Constantia
                  <br />
                  Cape Town
                </p>
              </div>
              <div className="mt-3 overflow-hidden border border-white/10">
                <iframe
                  src="https://www.google.com/maps?q=12+Peter+Cloete+Street,+Constantia,+Cape+Town,+South+Africa&output=embed"
                  width="100%" height="120" style={{border: 0}} allowFullScreen="" loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" title="Cape Town Office"
                  className="opacity-70 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div className="mt-12 pt-12 border-t border-white/10">
          <h4 className="text-white font-serif text-xl mb-6">Contact Us</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <a
              href="tel:+27415821022"
              className="flex items-center text-white/60 hover:text-brand-gold transition-colors"
            >
              <Phone className="h-5 w-5 text-brand-gold mr-3 flex-shrink-0" />
              <span className="font-sans text-sm">041 582 1022</span>
            </a>
            <a
              href="https://wa.me/27624313194"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-white/60 hover:text-brand-gold transition-colors"
            >
              <Phone className="h-5 w-5 text-brand-gold mr-3 flex-shrink-0" />
              <span className="font-sans text-sm">062 431 3194 (WhatsApp)</span>
            </a>
            <a
              href="mailto:nande@mhesheattorneys.co.za"
              className="flex items-center text-white/60 hover:text-brand-gold transition-colors"
            >
              <Mail className="h-5 w-5 text-brand-gold mr-3 flex-shrink-0" />
              <span className="font-sans text-sm">nande@mhesheattorneys.co.za</span>
            </a>
            <a
              href="mailto:admin@mhesheattorneys.co.za"
              className="flex items-center text-white/60 hover:text-brand-gold transition-colors"
            >
              <Mail className="h-5 w-5 text-brand-gold mr-3 flex-shrink-0" />
              <span className="font-sans text-sm">admin@mhesheattorneys.co.za</span>
            </a>
            <a
              href="mailto:infor@mhesheattorneys.co.za"
              className="flex items-center text-white/60 hover:text-brand-gold transition-colors"
            >
              <Mail className="h-5 w-5 text-brand-gold mr-3 flex-shrink-0" />
              <span className="font-sans text-sm">infor@mhesheattorneys.co.za</span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-white/40 font-sans text-sm">
              © {new Date().getFullYear()} Mheshe Attorneys Inc. All rights
              reserved.
            </p>
            <p className="text-white/40 font-sans text-sm mt-2 md:mt-0">
              Reg No. 2023/201575/21 | Chosen To Serve!
            </p>
          </div>
          <div className="text-center mt-4 pt-4 border-t border-white/5">
            <p className="text-white/30 font-sans text-xs">
              Design by{" "}
              <a
                href="https://www.successfuljournals.co.za/get-started"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-gold/70 hover:text-brand-gold transition-colors"
              >
                Successful Journals
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
